import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDrawer } from "@/context/DrawerContext";
import { TierBadge } from "@/components/TierBadge";
import { Pagination } from "@/components/Pagination";
import { MessageCircle, Phone, Search, Ghost, Zap, User, Clock, AlertCircle, LayoutGrid, List, Rows3, Bookmark, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["New Lead", "Connection Sent", "Connected", "Pitched", "future", "Details Sent", "Price Discussed", "Negotiation", "Won", "Lost"];
const TIERS = ["Award-Signal", "Momentum Builder", "Established Operator", "Low Fit"];
const TODAY = new Date().toISOString().slice(0, 10);
const VIEWS = [["kanban", LayoutGrid], ["list", List], ["compact", Rows3]];

// ---------- date helpers ----------
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dateLabel(iso) {
  if (!iso) return "No date";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function groupByDate(leads) {
  const groups = {};
  leads.forEach((l) => {
    const key = dateLabel(l.updated_date || l.created_date);
    if (!groups[key]) groups[key] = [];
    groups[key].push(l);
  });
  return groups; // preserves insertion order — leads already sorted by recency
}

export default function Pipeline() {
  const { user } = useAuth();
  const { openLead, refreshKey } = useDrawer();
  const [leads, setLeads] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [assignee, setAssignee] = useState("");
  const [reps, setReps] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [minScore, setMinScore] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, limit: 50 });
  const [drag, setDrag] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("siq_pipeline_view") || "kanban");
  const [savedViews, setSavedViews] = useState([]);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("siq_pipeline_view", v); };

  const loadViews = useCallback(() => api.get("/saved-views", { params: { page: "pipeline" } }).then((r) => setSavedViews(r.data)).catch(() => {}), []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. Hot A-tier in negotiation):");
    if (!name) return;
    const filters = { view, search, tier, assignee, stageFilter, minScore };
    try { await api.post("/saved-views", { page: "pipeline", name, filters }); toast.success(`Saved view "${name}"`); loadViews(); }
    catch { toast.error("Could not save view"); }
  };
  const applyView = (id) => {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    const f = v.filters || {};
    setViewPersist(f.view || "kanban");
    setSearch(f.search || ""); setTier(f.tier || ""); setAssignee(f.assignee || "");
    setStageFilter(f.stageFilter || ""); setMinScore(f.minScore || "");
    toast.success(`Loaded "${v.name}"`);
  };
  const deleteView = async (id, e) => { e.stopPropagation(); try { await api.delete(`/saved-views/${id}`); loadViews(); } catch { /* noop */ } };

  useEffect(() => {
    if (user?.role === "admin") api.get("/auth/users").then((r) => setReps(r.data)).catch(() => {});
  }, [user]);

  const loadReminders = useCallback(() => {
    api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (tier) params.tier = tier;
    if (assignee) params.assigned_to = assignee;
    if (minScore) params.min_score = Number(minScore);

    const byRecency = (arr) =>
      [...arr].sort((a, b) => new Date(b.updated_date || 0) - new Date(a.updated_date || 0));

    if (view === "list") {
      params.page = page;
      params.limit = 50;
      if (stageFilter) params.stage = stageFilter;
      api.get("/leads", { params }).then((r) => {
        setLeads(byRecency(r.data.items));
        setMeta({ total: r.data.total, pages: r.data.pages, limit: r.data.limit });
      });
    } else {
      api.get("/leads", { params }).then((r) => setLeads(byRecency(r.data)));
    }
  }, [search, tier, assignee, minScore, view, page, stageFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, tier, assignee, minScore, view, stageFilter]);
  useEffect(() => { loadReminders(); }, [loadReminders]);
  useEffect(() => { if (refreshKey) { load(); loadReminders(); } }, [refreshKey]);

  const byStage = (s) =>
    leads.filter((l) => l.stage === s)
         .sort((a, b) => new Date(b.updated_date || 0) - new Date(a.updated_date || 0));

  const due = reminders.filter((r) => r.status !== "upcoming");

  const move = async (id, stage, prevStage) => {
    if (!stage || stage === prevStage) return;
    const now = new Date().toISOString();
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, stage, updated_date: now } : l))
    );
    try {
      await api.patch(`/leads/${id}`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch {
      toast.error("Move failed");
      load();
    }
  };

  const drop = async (stage) => {
    if (!drag) return;
    const d = drag; setDrag(null);
    move(d.id, stage, d.stage);
  };

  return (
    <div className="h-screen flex flex-col">
      <header className="px-6 py-4 border-b border-zinc-200 bg-white/80 glass sticky top-0 z-10 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-black tracking-tight">Pipeline</h1>
          <p className="text-xs text-zinc-500">{view === "list" ? meta.total : leads.length} leads · {view === "kanban" ? "drag cards between stages" : view === "list" ? "grouped by date" : "grouped by tier"}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View switcher */}
          <div className="flex items-center gap-0.5 bg-zinc-100 rounded-lg p-0.5" data-testid="view-switcher">
            {VIEWS.map(([v, Icon]) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setViewPersist(v)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${view === v ? "bg-white text-klein shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}>
                <Icon className="h-3.5 w-3.5" /> {v}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
            <input data-testid="pipeline-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / company"
              className="pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm w-56 focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none transition" />
          </div>
          <select data-testid="pipeline-tier-filter" value={tier} onChange={(e) => setTier(e.target.value)}
            className="border border-zinc-300 rounded-lg text-sm px-3 py-2 focus:border-klein focus:outline-none">
            <option value="">All tiers</option>
            {TIERS.map((t) => <option key={t}>{t}</option>)}
          </select>
          {view !== "kanban" && (
            <select data-testid="pipeline-stage-filter" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
              className="border border-zinc-300 rounded-lg text-sm px-3 py-2 focus:border-klein focus:outline-none">
              <option value="">All stages</option>
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          )}
          <input data-testid="pipeline-minscore" type="number" min="0" max="100" value={minScore} onChange={(e) => setMinScore(e.target.value)} placeholder="Min score"
            className="w-24 border border-zinc-300 rounded-lg text-sm px-3 py-2 focus:border-klein focus:outline-none" />
          {user?.role === "admin" ? (
            <select data-testid="pipeline-rep-filter" value={assignee} onChange={(e) => setAssignee(e.target.value)}
              className="border border-zinc-300 rounded-lg text-sm px-3 py-2 focus:border-klein focus:outline-none">
              <option value="">All reps</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          ) : (
            <button data-testid="pipeline-my-leads" onClick={() => setAssignee(assignee ? "" : "me")}
              className={`flex items-center gap-1.5 border rounded-lg px-3 py-2 text-sm font-medium transition-colors ${assignee ? "bg-klein text-white border-klein" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>
              <User className="h-4 w-4" /> My leads
            </button>
          )}
          <button data-testid="save-view-btn" onClick={saveView} title="Save current filters as a view"
            className="flex items-center gap-1.5 border border-zinc-300 rounded-lg px-3 py-2 text-sm font-medium hover:border-klein hover:text-klein transition-colors">
            <Bookmark className="h-4 w-4" /> Save view
          </button>
        </div>
      </header>

      {savedViews.length > 0 && (
        <div className="px-6 py-2 border-b border-zinc-200 bg-white flex items-center gap-2 overflow-x-auto kanban-scroll" data-testid="saved-views">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 shrink-0">Views</span>
          {savedViews.map((v) => (
            <span key={v.id} className="shrink-0 inline-flex items-center gap-1 text-xs bg-zinc-100 hover:bg-zinc-200 rounded-full pl-3 pr-1 py-1 transition-colors">
              <button data-testid={`apply-view-${v.id}`} onClick={() => applyView(v.id)} className="font-medium">{v.name}</button>
              <button data-testid={`delete-view-${v.id}`} onClick={(e) => deleteView(v.id, e)} className="text-zinc-400 hover:text-red-600 rounded-full p-0.5"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {due.length > 0 && (
        <div className="px-6 py-2.5 bg-red-50 border-b border-red-200 flex items-center gap-3 overflow-x-auto kanban-scroll" data-testid="due-tasks">
          <div className="flex items-center gap-1.5 text-red-700 shrink-0">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wide">{due.length} follow-up{due.length > 1 ? "s" : ""} due</span>
          </div>
          {due.slice(0, 12).map((r) => (
            <button key={r.id} data-testid={`due-${r.id}`} onClick={() => openLead(r.id)}
              className={`shrink-0 flex items-center gap-1.5 border px-2.5 py-1 text-xs font-medium transition-colors ${r.status === "overdue" ? "bg-red-600 text-white border-red-700 hover:bg-red-500" : "bg-white border-red-300 text-red-800 hover:bg-red-100"}`}>
              <Clock className="h-3 w-3" /> {r.name} · {r.status === "overdue" ? "overdue" : "today"}{r.follow_up_time ? ` ${r.follow_up_time}` : ""}
            </button>
          ))}
        </div>
      )}

      {view === "kanban" && (
        <div className="flex-1 overflow-x-auto overflow-y-hidden kanban-scroll p-3" data-testid="kanban-view">
          <div className="flex h-full gap-3 min-w-max">
            {STAGES.map((stage) => {
              const items = byStage(stage);
              return (
                <div key={stage} data-testid={`col-${stage.replace(/\s/g, "-").toLowerCase()}`}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => drop(stage)}
                  className="w-72 shrink-0 bg-zinc-100/70 rounded-2xl flex flex-col border border-zinc-200/70">
                  <div className="px-3.5 py-2.5 flex items-center justify-between sticky top-0">
                    <span className="text-xs font-bold uppercase tracking-wide text-zinc-600">{stage}</span>
                    <span className="font-mono text-[11px] bg-white text-zinc-600 rounded-full px-2 py-0.5 border border-zinc-200">{items.length}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 kanban-scroll">
                    {items.map((l) => (
                      <LeadCard key={l.id} lead={l} onOpen={() => openLead(l.id)} onDragStart={() => setDrag(l)} />
                    ))}
                    {items.length === 0 && <div className="text-[11px] text-zinc-400 text-center py-6 font-mono">empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="flex-1 flex flex-col min-h-0">
          <ListView leads={leads} openLead={openLead} move={move} />
          <div className="px-4 py-2 border-t border-zinc-200 bg-white">
            <Pagination page={page} pages={meta.pages} total={meta.total} limit={meta.limit} onPage={setPage} />
          </div>
        </div>
      )}
      {view === "compact" && <CompactView leads={stageFilter ? leads.filter((l) => l.stage === stageFilter) : leads} openLead={openLead} />}
    </div>
  );
}

function dueBadge(lead) {
  const fu = (lead.follow_up_date || "").slice(0, 10);
  if (!fu) return null;
  return fu < TODAY ? "overdue" : fu === TODAY ? "today" : "upcoming";
}

function LeadCard({ lead, onOpen, onDragStart }) {
  const digits = (lead.phone || "").replace(/\D/g, "");
  const high = lead.next_action_priority === "high";
  const fu = (lead.follow_up_date || "").slice(0, 10);
  const dueState = dueBadge(lead);
  return (
    <div draggable onDragStart={onDragStart} onClick={onOpen} data-testid={`lead-card-${lead.id}`}
      className="group bg-white border border-zinc-200 rounded-xl p-3 cursor-pointer hover:-translate-y-0.5 hover:hard-shadow hover:border-zinc-300 transition-all duration-200 rise">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{lead.name}</div>
          <div className="text-xs text-zinc-500 truncate">{lead.company}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-base font-bold leading-none tabular-nums">{lead.fit_score}</div>
        </div>
      </div>

      {/* Date row */}
      <div className="flex items-center gap-1 mt-1.5 text-[10px] font-mono text-zinc-400" data-testid={`card-date-${lead.id}`}>
        <CalendarDays className="h-3 w-3" />
        {shortDate(lead.updated_date || lead.created_date)}
      </div>

      <div className="flex items-center justify-between mt-2.5">
        <TierBadge tier={lead.tier} />
        <div className="flex items-center gap-1.5">
          {dueState && dueState !== "upcoming" && (
            <span data-testid={`card-due-${lead.id}`} className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-md border ${dueState === "overdue" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              <Clock className="h-2.5 w-2.5" /> {dueState}
            </span>
          )}
          {dueState === "upcoming" && <Clock className="h-3.5 w-3.5 text-zinc-300" title={`Follow-up ${fu}`} />}
          {lead.ghost_point && <Ghost className="h-3.5 w-3.5 text-zinc-400" title={lead.ghost_point} />}
        </div>
      </div>
      {lead.next_action && (
        <div className={`mt-2.5 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5 ${high ? "bg-[#FFF1EB] text-[#B23600]" : "bg-zinc-50 text-zinc-600"}`}>
          {high && <Zap className="h-3 w-3 mt-0.5 shrink-0" />}
          <span className="text-[11px] leading-tight font-medium">{lead.next_action}</span>
        </div>
      )}
      <div className="flex gap-1.5 mt-2.5 opacity-70 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <a href={digits ? `https://wa.me/${digits}` : "#"} target="_blank" rel="noreferrer" data-testid={`card-wa-${lead.id}`}
          className="flex-1 flex items-center justify-center py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors">
          <MessageCircle className="h-3.5 w-3.5 text-emerald-700" />
        </a>
        <a href={lead.phone ? `tel:${lead.phone}` : "#"} data-testid={`card-call-${lead.id}`}
          className="flex-1 flex items-center justify-center py-1.5 rounded-lg bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 transition-colors">
          <Phone className="h-3.5 w-3.5 text-zinc-700" />
        </a>
      </div>
    </div>
  );
}

function ListView({ leads, openLead, move }) {
  const cols = [["name", "Name"], ["company", "Company"], ["fit_score", "Score"], ["tier", "Tier"], ["stage", "Stage"], ["assigned_to_name", "Owner"]];
  const grouped = groupByDate(leads); // leads already sorted by recency from Pipeline's load()

  return (
    <div className="flex-1 overflow-auto kanban-scroll" data-testid="list-view">
      {Object.keys(grouped).length === 0 && (
        <div className="text-center text-zinc-400 font-mono py-10 text-xs">No leads match your filters.</div>
      )}
      {Object.entries(grouped).map(([dateKey, items]) => (
        <div key={dateKey} className="mb-2">
          <div className="sticky top-0 z-[1] bg-zinc-100 border-y border-zinc-200 px-4 py-1.5 flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-zinc-500" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-600">{dateKey}</span>
            <span className="text-[10px] font-mono text-zinc-400">{items.length} lead{items.length > 1 ? "s" : ""}</span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-white border-b border-zinc-200">
              <tr>
                {cols.map(([k, label]) => (
                  <th key={k} className="text-left px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-zinc-500 whitespace-nowrap">
                    {label}
                  </th>
                ))}
                <th className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-zinc-500">Due</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const dueState = dueBadge(l);
                return (
                  <tr key={l.id} data-testid={`list-row-${l.id}`} onClick={() => openLead(l.id)}
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors">
                    <td className="px-4 py-2.5 font-medium">{l.name}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{l.company || "—"}</td>
                    <td className="px-4 py-2.5 font-mono font-bold tabular-nums">{l.fit_score}</td>
                    <td className="px-4 py-2.5"><TierBadge tier={l.tier} /></td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <select data-testid={`list-stage-${l.id}`} value={l.stage} onChange={(e) => move(l.id, e.target.value, l.stage)}
                        className="border border-zinc-300 rounded-md text-xs px-2 py-1 bg-white focus:border-klein focus:outline-none">
                        {STAGES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600 text-xs">{l.assigned_to_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      {dueState && dueState !== "upcoming" ? (
                        <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-md border ${dueState === "overdue" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          <Clock className="h-2.5 w-2.5" /> {dueState}
                        </span>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function CompactView({ leads, openLead }) {
  return (
    <div className="flex-1 overflow-auto kanban-scroll p-5 space-y-6" data-testid="compact-view">
      {TIERS.map((t) => {
        const items = leads.filter((l) => l.tier === t).sort((a, b) => new Date(b.updated_date || 0) - new Date(a.updated_date || 0));
        if (items.length === 0) return null;
        return (
          <div key={t}>
            <div className="flex items-center gap-2 mb-2.5">
              <TierBadge tier={t} />
              <span className="font-mono text-[11px] text-zinc-500">{items.length}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
              {items.map((l) => {
                const dueState = dueBadge(l);
                return (
                  <button key={l.id} data-testid={`compact-${l.id}`} onClick={() => openLead(l.id)}
                    className="text-left bg-white border border-zinc-200 rounded-xl p-3 hover:-translate-y-0.5 hover:hard-shadow hover:border-zinc-300 transition-all duration-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{l.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{l.company || "—"}</div>
                      </div>
                      <div className="font-mono text-base font-bold leading-none tabular-nums shrink-0">{l.fit_score}</div>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-[10px] font-mono text-zinc-400">
                      <CalendarDays className="h-3 w-3" /> {shortDate(l.updated_date || l.created_date)}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] font-mono uppercase text-zinc-400">{l.stage}</span>
                      {dueState && dueState !== "upcoming" && (
                        <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-md border ${dueState === "overdue" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          <Clock className="h-2.5 w-2.5" /> {dueState}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {leads.length === 0 && <div className="text-center text-zinc-400 font-mono py-10 text-xs">No leads match your filters.</div>}
    </div>
  );
}